/**
 * SHARED Checkout Store
 * One source of truth for all checkout-related state
 * Uses shared useOrderPricing with Singleton pattern
 * Replaces complex watcher cascades with simple reactive state
 *
 * ✅ GENERIC: Works for all projects via useProjectConfig
 */
import { defineStore } from 'pinia';
import { computed, ref, watch, inject } from 'vue';
import { useCartStore } from '../composables/useCartStore';
import { useOrderPricing } from '../composables/useOrderPricing';
import { useProjectConfig } from '../composables/useProjectConfig';
// ✅ Using shared types from useOrderPricing - no need for local interface
export const useCheckoutStore = defineStore('checkout', () => {
    const cartStore = useCartStore();
    // ✅ USE PROJECT CONFIG: Get project name dynamically
    const { projectId } = useProjectConfig();
    const projectName = projectId.value || 'default';
    // ✅ USE SHARED SINGLETON: Get the shared pricing composable instance
    const sharedPricing = useOrderPricing(projectName);
    // ✅ INJECT API CLIENT: Must be at top level of setup
    const apiClient = inject('apiClient');
    // ✅ SIMPLE ORDER TYPE STATE - sync with cart
    const orderType = ref(cartStore.deliveryType || 'pickup');
    // ✅ PAYMENT METHODS STATE
    const paymentMethods = ref(null);
    const selectedPaymentMethod = ref('manual');
    const paymentMethodsLoading = ref(false);
    // ✅ DISCOUNT CODE STATE
    const discountCode = ref('');
    const discountInfo = ref(null);
    const discountFromUrl = ref(false);
    const discountValidating = ref(false);
    const discountError = ref('');
    const getDeliveryAddressForType = (type) => {
        if (type !== 'delivery') {
            console.log('📍 [checkout-store] getDeliveryAddressForType: Not delivery type, returning undefined', { type });
            return undefined;
        }
        const { street, city, postalCode } = cartStore.deliveryAddress;
        if (!street?.trim() || !city?.trim() || !postalCode?.trim()) {
            console.warn('⚠️ [checkout-store] getDeliveryAddressForType: Incomplete address, returning undefined', {
                hasStreet: !!street?.trim(),
                hasCity: !!city?.trim(),
                hasPostalCode: !!postalCode?.trim(),
                fullAddress: { street, city, postalCode }
            });
            return undefined;
        }
        const formattedAddress = `${street.trim()}, ${postalCode.trim()} ${city.trim()}`;
        console.log('✅ [checkout-store] getDeliveryAddressForType: Using delivery address for pricing:', formattedAddress);
        return formattedAddress;
    };
    // ✅ USE SHARED SINGLETON COMPUTED VALUES
    const { pricingData, isLoading, error, requestStatus, minimumOrderInfo, isMinimumOrderReached, calculateOrderPricing, clearPricing, clearDiscountCode: clearOrderPricingDiscount // ✅ Import clearDiscountCode from useOrderPricing
     } = sharedPricing;
    // ✅ MANUAL TIMING REFS: Fix broken computed properties with manual updates
    const manualPickupTime = ref('Wird berechnet...');
    const manualDeliveryTime = ref('Wird berechnet...');
    // ✅ NEUE ESTIMATED TIME REFS: Für Modal und Buttons
    const estimatedPickupTime = ref('Wird berechnet...');
    const estimatedDeliveryTime = ref('Wird berechnet...');
    const estimatedPickupDateTime = ref('');
    const estimatedDeliveryDateTime = ref('');
    // ✅ OPERATING HOURS REFS: Für Modal
    const operatingHours = ref([]);
    // ✅ USER SELECTED TIMES: Separate Zeiten für Pickup und Delivery
    const selectedPickupTime = ref('');
    const selectedDeliveryTime = ref('');
    // ✅ TIME VALIDATION STATE: Server-Validierung für gewählte Zeiten
    const timeValidationLoading = ref(false);
    const timeValidationError = ref(null);
    const lastValidatedTime = ref(null);
    // ✅ ZENTRALE ZEITBERECHNUNG: Aktuelle Zeit für alle Berechnungen
    const currentTime = ref(new Date());
    // ✅ Update aktuelle Zeit jede Minute
    let timeUpdateInterval = null;
    const startTimeUpdates = () => {
        if (timeUpdateInterval)
            clearInterval(timeUpdateInterval);
        timeUpdateInterval = setInterval(() => {
            currentTime.value = new Date();
            console.log('⏰ [checkout-store] Time updated:', currentTime.value.toLocaleTimeString());
        }, 60000); // Jede Minute
        console.log('✅ [checkout-store] Time updates started');
    };
    // ✅ ZENTRALE ZEITFENSTER BERECHNUNG: Für TimeSelectionForm
    const currentTimeWindow = computed(() => {
        const now = currentTime.value;
        const nowMinutes = now.getHours() * 60 + now.getMinutes();
        console.log('🕐 [checkout-store] currentTimeWindow computation:', {
            now: now.toLocaleTimeString(),
            nowMinutes,
            orderType: orderType.value,
            selectedPickupTime: selectedPickupTime.value,
            selectedDeliveryTime: selectedDeliveryTime.value,
            estimatedPickupTime: estimatedPickupTime.value,
            estimatedDeliveryTime: estimatedDeliveryTime.value
        });
        // ✅ PRIORITÄT 1: Nutzer hat spezifische Zeit für aktuellen Order Type gewählt
        if (orderType.value === 'pickup' && selectedPickupTime.value) {
            console.log('✅ [checkout-store] Using selected pickup time:', selectedPickupTime.value);
            return {
                display: `um ${selectedPickupTime.value} Uhr`,
                startTime: selectedPickupTime.value,
                endTime: selectedPickupTime.value,
                isRange: false,
                prepTime: 0,
                isUserSelected: true
            };
        }
        if (orderType.value === 'delivery' && selectedDeliveryTime.value) {
            console.log('✅ [checkout-store] Using selected delivery time:', selectedDeliveryTime.value);
            return {
                display: `um ${selectedDeliveryTime.value} Uhr`,
                startTime: selectedDeliveryTime.value,
                endTime: selectedDeliveryTime.value,
                isRange: false,
                prepTime: 0,
                isUserSelected: true
            };
        }
        // Helper: Zeit zu Minuten
        const timeToMinutes = (time) => {
            if (!time || !time.includes(':'))
                return 0;
            const [hours, minutes] = time.split(':').map(Number);
            return hours * 60 + minutes;
        };
        // Helper: Minuten zu Zeit
        const minutesToTime = (minutes) => {
            const hours = Math.floor(minutes / 60);
            const mins = minutes % 60;
            return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`;
        };
        if (orderType.value === 'pickup' && estimatedPickupTime.value !== 'Wird berechnet...') {
            // Parse Server-Zeitfenster: "20:28 - 20:34 Uhr"
            const timeRangeMatch = estimatedPickupTime.value.match(/(\d{1,2}:\d{2})\s*-\s*(\d{1,2}:\d{2})/);
            const singleTimeMatch = estimatedPickupTime.value.match(/(\d{1,2}:\d{2})/);
            if (timeRangeMatch) {
                const startTime = timeRangeMatch[1]; // z.B. "20:32"
                const endTime = timeRangeMatch[2]; // z.B. "20:37"
                const startMinutes = timeToMinutes(startTime);
                const prepTime = startMinutes - nowMinutes; // Zeit bis Pickup-Start
                // ✅ Server-Zeitfenster direkt verwenden
                const result = {
                    display: `${startTime} - ${endTime} Uhr`,
                    startTime,
                    endTime,
                    isRange: true,
                    prepTime
                };
                console.log('✅ [checkout-store] Pickup time window (direct server):', result);
                return result;
            }
            else if (singleTimeMatch) {
                const time = singleTimeMatch[1];
                const timeMinutes = timeToMinutes(time);
                if (timeMinutes > nowMinutes) {
                    const result = {
                        display: `um ${time} Uhr`,
                        startTime: time,
                        endTime: time,
                        isRange: false,
                        prepTime: timeMinutes - nowMinutes
                    };
                    console.log('✅ [checkout-store] Single pickup time calculated:', result);
                    return result;
                }
            }
        }
        if (orderType.value === 'delivery' && estimatedDeliveryTime.value !== 'Wird berechnet...') {
            // Parse Server-Zeitfenster: "20:34 - 21:54 Uhr"
            const timeRangeMatch = estimatedDeliveryTime.value.match(/(\d{1,2}:\d{2})\s*-\s*(\d{1,2}:\d{2})/);
            const singleTimeMatch = estimatedDeliveryTime.value.match(/(\d{1,2}:\d{2})/);
            if (timeRangeMatch) {
                const startTime = timeRangeMatch[1]; // z.B. "17:25"
                const endTime = timeRangeMatch[2]; // z.B. "22:07"
                const startMinutes = timeToMinutes(startTime);
                const deliveryTime = startMinutes - nowMinutes; // Zeit bis Delivery-Start
                // ✅ VALIDATION: Prüfe ob Server-Zeit realistisch ist (nicht in der Vergangenheit)
                if (startMinutes > nowMinutes) {
                    // Server-Zeitfenster verwenden
                    const result = {
                        display: `${startTime} - ${endTime} Uhr`,
                        startTime,
                        endTime,
                        isRange: true,
                        prepTime: deliveryTime
                    };
                    console.log('✅ [checkout-store] Delivery time window (valid server):', result);
                    return result;
                }
                else {
                    // Server-Zeit ist unrealistisch - lokale Berechnung mit realistischen Zeiten
                    console.warn('⚠️ [checkout-store] Server delivery time is in the past, using local calculation');
                    const minPrepMinutes = 25; // Mindest-Lieferzeit
                    const maxPrepMinutes = 105; // Maximal-Lieferzeit  
                    const localStartMinutes = nowMinutes + minPrepMinutes;
                    const localEndMinutes = nowMinutes + maxPrepMinutes;
                    const localStartTime = minutesToTime(localStartMinutes);
                    const localEndTime = minutesToTime(localEndMinutes);
                    const result = {
                        display: `${localStartTime} - ${localEndTime} Uhr`,
                        startTime: localStartTime,
                        endTime: localEndTime,
                        isRange: true,
                        prepTime: minPrepMinutes
                    };
                    console.log('✅ [checkout-store] Delivery time window (local calculation):', result);
                    return result;
                }
            }
            else if (singleTimeMatch) {
                const time = singleTimeMatch[1];
                const timeMinutes = timeToMinutes(time);
                if (timeMinutes > nowMinutes) {
                    const result = {
                        display: `um ${time} Uhr`,
                        startTime: time,
                        endTime: time,
                        isRange: false,
                        prepTime: timeMinutes - nowMinutes
                    };
                    console.log('✅ [checkout-store] Single delivery time calculated:', result);
                    return result;
                }
            }
        }
        // Fallback: Standard-Pufferzeiten
        const bufferMinutes = orderType.value === 'pickup' ? 30 : 60;
        const endMinutes = nowMinutes + bufferMinutes;
        const endTime = minutesToTime(endMinutes);
        const startTime = minutesToTime(nowMinutes);
        const fallbackResult = {
            display: `${startTime} - ${endTime} Uhr`,
            startTime,
            endTime,
            isRange: true,
            prepTime: bufferMinutes
        };
        console.log('⚠️ [checkout-store] Fallback time window calculated:', fallbackResult);
        return fallbackResult;
    });
    // ✅ MANUAL PRICING REFS: Fix the same reactivity issue for pricing values  
    const manualSubtotal = ref(0);
    const manualDeliveryFee = ref(0);
    const manualServiceFee = ref(0);
    const manualTotal = ref(0);
    // ✅ TAX CALCULATION REFS: Store tax data from server response
    const taxBreakdown = ref({});
    const taxCalculation = ref(null);
    const totalTax = ref(0);
    // ✅ MANUAL VALIDATION REFS: Fix canSubmit reactivity
    const manualIsLoading = ref(false);
    const manualIsMinimumOrderReached = ref(false);
    // ✅ HAS COMPLETE DELIVERY ADDRESS: Check if we have a full address for delivery pricing
    const hasCompleteDeliveryAddress = computed(() => {
        if (orderType.value !== 'delivery') {
            return true; // Not needed for pickup
        }
        const { street, city, postalCode } = cartStore.deliveryAddress;
        const isComplete = !!(street?.trim() && city?.trim() && postalCode?.trim());
        console.log('📍 [checkout-store] hasCompleteDeliveryAddress:', {
            orderType: orderType.value,
            isComplete,
            address: { street: !!street?.trim(), city: !!city?.trim(), postalCode: !!postalCode?.trim() }
        });
        return isComplete;
    });
    // ✅ OPENING HOURS COMPUTED: Extract from pricing data - FIXED
    const openingHoursInfo = computed(() => {
        const pricingData = sharedPricing.pricingData.value;
        const hoursInfo = pricingData?.data?.validation?.openingHours || null;
        console.log('🕐 [checkout-store] openingHoursInfo computed:', {
            hasPricingData: !!pricingData,
            hasData: !!pricingData?.data,
            hasValidation: !!pricingData?.data?.validation,
            hasOpeningHours: !!hoursInfo,
            openingHoursData: hoursInfo,
            fullPricingData: pricingData
        });
        return hoursInfo;
    });
    // ✅ MANUAL OPENING HOURS REF: Fix reactivity issue
    const manualOpeningHours = ref(null);
    // ✅ UPDATE OPENING HOURS manually when pricingData changes
    const updateOpeningHours = () => {
        const pricingData = sharedPricing.pricingData.value;
        const hoursInfo = pricingData?.data?.validation?.openingHours || null;
        console.log('🕐 [checkout-store] updateOpeningHours called:', {
            hasPricingData: !!pricingData,
            hasOpeningHours: !!hoursInfo,
            openingHoursData: hoursInfo
        });
        manualOpeningHours.value = hoursInfo;
    };
    // ✅ UPDATE PAYMENT METHODS manually when pricingData changes
    const updatePaymentMethods = () => {
        const pricingData = sharedPricing.pricingData.value;
        // ✅ FIX: Payment methods are at root level, not inside data
        const paymentMethodsData = pricingData?.paymentMethods || pricingData?.data?.paymentMethods || null;
        if (paymentMethodsData) {
            paymentMethods.value = paymentMethodsData;
            // Auto-select default payment method if none selected
            if (!selectedPaymentMethod.value || selectedPaymentMethod.value === 'manual') {
                if (paymentMethodsData.manual?.enabled || paymentMethodsData.manual?.isDefault) {
                    selectedPaymentMethod.value = 'manual';
                }
                else if (paymentMethodsData.onlinePaymentAvailable) {
                    selectedPaymentMethod.value = 'online';
                }
                else if (paymentMethodsData.paypal?.enabled) {
                    selectedPaymentMethod.value = 'paypal';
                }
            }
        }
        else {
            // Fallback to default configuration
            paymentMethods.value = {
                onlinePaymentsEnabled: false,
                preferredOnlinePaymentProvider: 'paypal',
                onlinePaymentAvailable: false,
                activeOnlineProvider: null,
                fallbackProvider: null,
                displayLabel: 'Jetzt online bezahlen',
                supportedPaymentMethodIcons: ['visa', 'mastercard', 'apple_pay', 'google_pay', 'klarna', 'sepa', 'paypal'],
                manual: {
                    enabled: true,
                    isDefault: true
                },
                cardByPickup: {
                    enabled: false
                },
                cardByDelivery: {
                    enabled: false
                },
                paypal: {
                    enabled: false,
                    fundingSources: {}
                },
                stripe: {
                    enabled: false
                }
            };
            selectedPaymentMethod.value = 'manual';
        }
    };
    // ✅ DEBUG: Watch openingHoursInfo changes
    watch(() => openingHoursInfo.value, (newHoursInfo) => {
        console.log('🕐 [checkout-store] openingHoursInfo changed:', {
            newValue: newHoursInfo,
            isWithinHours: newHoursInfo?.isWithinHours,
            status: newHoursInfo?.status,
            message: newHoursInfo?.message
        });
    }, { immediate: true });
    // ✅ UPDATE TIMING VALUES function
    const updateTimingValues = () => {
        const currentPricingData = sharedPricing.pricingData.value;
        const data = currentPricingData?.data;
        console.log('⏰ [checkout-store] updateTimingValues called:', {
            hasPricingData: !!currentPricingData,
            hasData: !!data,
            hasDeliveryInfo: !!data?.delivery,
            pickupTimeData: data?.delivery?.pickupTime,
            deliveryTimeData: data?.delivery?.deliveryTime
        });
        // Update pickup timing
        const pickupTiming = data?.delivery?.pickupTime;
        if (pickupTiming) {
            const { min, max } = pickupTiming;
            manualPickupTime.value = min === max ? `${min} Min` : `${min}-${max} Min`;
            console.log('✅ [checkout-store] Updated pickup time to:', manualPickupTime.value);
        }
        else {
            manualPickupTime.value = 'Wird berechnet...';
        }
        // Update delivery timing
        const deliveryTiming = data?.delivery?.deliveryTime;
        if (deliveryTiming) {
            const { min, max } = deliveryTiming;
            manualDeliveryTime.value = min === max ? `${min} Min` : `${min}-${max} Min`;
            console.log('✅ [checkout-store] Updated delivery time to:', manualDeliveryTime.value);
        }
        else {
            manualDeliveryTime.value = 'Wird berechnet...';
        }
        // ✅ UPDATE ESTIMATED TIMES: Die exakten Zeiten für das Modal
        const estimatedPickup = data?.delivery?.estimatedPickupTime;
        if (estimatedPickup?.display) {
            estimatedPickupTime.value = estimatedPickup.display;
            estimatedPickupDateTime.value = estimatedPickup.min || estimatedPickup.max || '';
            console.log('✅ [checkout-store] Updated estimated pickup time to:', estimatedPickupTime.value);
        }
        else {
            estimatedPickupTime.value = 'Wird berechnet...';
            estimatedPickupDateTime.value = '';
        }
        const estimatedDelivery = data?.delivery?.estimatedDeliveryTime;
        if (estimatedDelivery?.display) {
            estimatedDeliveryTime.value = estimatedDelivery.display;
            estimatedDeliveryDateTime.value = estimatedDelivery.min || estimatedDelivery.max || '';
            console.log('✅ [checkout-store] Updated estimated delivery time to:', estimatedDeliveryTime.value);
        }
        else {
            estimatedDeliveryTime.value = 'Wird berechnet...';
            estimatedDeliveryDateTime.value = '';
        }
        // ✅ UPDATE OPERATING HOURS: Für das Modal
        const availableSlots = data?.delivery?.availableTimeSlots;
        if (availableSlots?.enabled && availableSlots?.hours?.length) {
            operatingHours.value = availableSlots.hours.map((slot) => ({
                open: slot.start,
                close: slot.end
            }));
            console.log('✅ [checkout-store] Updated operating hours:', operatingHours.value);
        }
        else {
            operatingHours.value = [];
        }
        // ✅ UPDATE PRICING VALUES - Extract from cent-based API response
        const pricingData = data?.pricing;
        if (pricingData) {
            console.log('🔍 [checkout-store] Found pricing object:', pricingData);
            // ✅ CONVERT FROM CENT TO EURO: API returns values in cent
            manualSubtotal.value = (pricingData.subtotalCent || 0) / 100;
            manualDeliveryFee.value = (pricingData.deliveryFeeCent || 0) / 100;
            manualServiceFee.value = (pricingData.serviceFeeCent || 0) / 100;
            manualTotal.value = (pricingData.totalCent || 0) / 100;
            // ✅ EXTRACT TAX DATA: Process tax information from server response
            const taxData = data?.tax;
            if (taxData) {
                totalTax.value = (taxData.totalCent || 0) / 100;
                // ✅ ENHANCE TAX BREAKDOWN: Convert cent values to Euro for UI compatibility
                const enhancedBreakdown = {};
                if (taxData.breakdown) {
                    Object.entries(taxData.breakdown).forEach(([rate, rateData]) => {
                        enhancedBreakdown[rate] = {
                            ...rateData,
                            taxAmount: (rateData.taxAmountCent || 0) / 100, // Add Euro value
                            netAmount: (rateData.netAmountCent || 0) / 100, // Add Euro value
                            grossAmount: (rateData.grossAmountCent || 0) / 100 // Add Euro value
                        };
                    });
                }
                taxBreakdown.value = enhancedBreakdown;
                // ✅ ENHANCE TAX CALCULATION: Add totalTax property for UI compatibility
                taxCalculation.value = {
                    ...taxData,
                    totalTax: totalTax.value, // Add the Euro-converted totalTax property
                    breakdown: enhancedBreakdown // Use enhanced breakdown with Euro values
                };
                console.log('✅ [checkout-store] Updated tax data:', {
                    totalTax: totalTax.value,
                    breakdown: taxBreakdown.value,
                    taxCalculation: taxCalculation.value
                });
            }
            else {
                // Clear tax data if not available
                taxBreakdown.value = {};
                taxCalculation.value = null;
                totalTax.value = 0;
            }
            console.log('✅ [checkout-store] Updated pricing values from CENT values:', {
                subtotal: manualSubtotal.value,
                deliveryFee: manualDeliveryFee.value,
                serviceFee: manualServiceFee.value,
                total: manualTotal.value,
                centValues: {
                    subtotalCent: pricingData.subtotalCent,
                    deliveryFeeCent: pricingData.deliveryFeeCent,
                    serviceFeeCent: pricingData.serviceFeeCent,
                    totalCent: pricingData.totalCent
                }
            });
        }
        else {
            console.log('❌ [checkout-store] No pricing data found in API response:', {
                hasData: !!data,
                dataKeys: data ? Object.keys(data) : [],
                fullDataStructure: data
            });
            // Reset to zero if no pricing data
            manualSubtotal.value = 0;
            manualDeliveryFee.value = 0;
            manualServiceFee.value = 0;
            manualTotal.value = 0;
            // Reset tax data
            taxBreakdown.value = {};
            taxCalculation.value = null;
            totalTax.value = 0;
        }
        // ✅ UPDATE VALIDATION STATE: Extract from shared composable
        manualIsLoading.value = isLoading.value || false;
        manualIsMinimumOrderReached.value = isMinimumOrderReached.value || false;
        // ✅ UPDATE OPENING HOURS: Extract from pricing data  
        updateOpeningHours();
        // ✅ UPDATE PAYMENT METHODS: Extract from pricing data
        updatePaymentMethods();
        console.log('✅ [checkout-store] Updated validation state:', {
            isLoading: manualIsLoading.value,
            isMinimumOrderReached: manualIsMinimumOrderReached.value,
            sharedLoading: isLoading.value,
            sharedMinOrder: isMinimumOrderReached.value,
            openingHours: manualOpeningHours.value,
            paymentMethods: paymentMethods.value
        });
    };
    // ✅ LISTEN TO PRICING EVENTS - React to API responses
    window.addEventListener('orderPricingChanged', () => {
        console.log('🎉 [checkout-store] Received orderPricingChanged event');
        updateTimingValues();
    });
    // ✅ INITIAL UPDATE on store creation
    updateTimingValues();
    // ✅ WATCH FOR PRICING DATA CHANGES: Fix Page Reload issue
    watch(() => sharedPricing.pricingData.value, (newPricingData) => {
        if (newPricingData) {
            console.log('🔄 [checkout-store] Pricing data changed, updating values:', newPricingData?.code);
            updateTimingValues();
        }
    }, { immediate: true, deep: true });
    // ✅ PAYMENT METHODS COMPUTED PROPERTIES
    const availablePaymentMethods = computed(() => {
        if (!paymentMethods.value)
            return [];
        const methods = [];
        // Manual payment (cash/card on delivery/pickup)
        if (paymentMethods.value.manual?.enabled) {
            const isPickup = orderType.value === 'pickup';
            const cardText = getCardPaymentText();
            methods.push({
                key: 'manual',
                name: 'Manuelle Zahlung',
                description: `Bar${cardText} bei ${isPickup ? 'Abholung' : 'Lieferung'}`,
                icon: '💰',
                enabled: true,
                isDefault: paymentMethods.value.manual.isDefault
            });
        }
        // PayPal
        if (paymentMethods.value.paypal?.enabled) {
            methods.push({
                key: 'paypal',
                name: 'PayPal',
                description: 'Direkt sicher online bezahlen',
                icon: '💙',
                enabled: true,
                config: paymentMethods.value.paypal
            });
        }
        return methods;
    });
    const hasPaymentMethods = computed(() => availablePaymentMethods.value.length > 0);
    // Dynamische Anzeige für Kartenzahlung basierend auf Delivery-Type und ProjectSettings
    const getCardPaymentText = () => {
        if (!paymentMethods.value) {
            return '';
        }
        const isPickup = orderType.value === 'pickup';
        const isDelivery = orderType.value === 'delivery';
        const cardByPickup = paymentMethods.value.cardByPickup?.enabled;
        const cardByDelivery = paymentMethods.value.cardByDelivery?.enabled;
        // ✅ Return nur den Zusatztext für Kartenzahlung, nicht den vollständigen Text
        if ((isPickup && cardByPickup) || (isDelivery && cardByDelivery)) {
            return ' oder Debitkarte';
        }
        return '';
    };
    // ✅ PAYMENT METHODS ACTIONS
    const selectPaymentMethod = (method) => {
        selectedPaymentMethod.value = method;
    };
    const clearPaymentData = () => {
        selectedPaymentMethod.value = 'manual';
    };
    // ✅ DISCOUNT CODE ACTIONS
    const setDiscountCode = (code) => {
        discountCode.value = code;
        console.log('🎟️ [checkout-store] Discount code set:', code);
    };
    const setDiscountFromUrl = (code) => {
        discountCode.value = code;
        discountFromUrl.value = true;
        console.log('🎟️ [checkout-store] Discount code set from URL:', code);
    };
    const clearDiscountCode = () => {
        discountCode.value = '';
        discountInfo.value = null;
        discountFromUrl.value = false;
        discountError.value = '';
        console.log('🗑️ [checkout-store] Discount code cleared');
    };
    const updateDiscountInfo = (info) => {
        discountInfo.value = info;
        console.log('✅ [checkout-store] Discount info updated:', info);
    };
    const applyDiscountCode = async (code) => {
        if (!code || !code.trim()) {
            discountError.value = 'Bitte geben Sie einen Rabattcode ein';
            return;
        }
        if (!apiClient) {
            discountError.value = 'API Client nicht verfügbar';
            return;
        }
        discountValidating.value = true;
        discountError.value = '';
        try {
            const normalizedCode = code.toUpperCase().trim();
            console.log('🎟️ [checkout-store] Validating discount code:', normalizedCode);
            const result = await apiClient.validateDiscountCode(normalizedCode, manualTotal.value);
            if (result.success && result.discount) {
                const discount = result.discount;
                const amountCent = discount.amountCent ?? 0;
                discountInfo.value = {
                    isValid: true,
                    code: discount.code || normalizedCode,
                    name: discount.name || discount.code || normalizedCode,
                    description: discount.description,
                    minimumOrderValueCent: discount.minimumOrderValueCent,
                    amountCent,
                    amountEuro: amountCent / 100,
                    type: discount.type || 'discount',
                    message: discount.message,
                };
                discountCode.value = normalizedCode;
                console.log('✅ [checkout-store] Discount code applied:', discountInfo.value);
                // ✅ WICHTIG: Trigger order recalculation with discount code
                console.log('🔄 [checkout-store] Triggering order recalculation with discount...');
                await calculateOrderPricing(cartStore.items, orderType.value, getDeliveryAddressForType(orderType.value), discountCode.value // ✅ Pass discount code to pricing calculation
                );
                console.log('✅ [checkout-store] Order recalculated with discount');
            }
            else {
                discountError.value = result.message || 'Ungültiger Rabattcode';
                discountInfo.value = null;
            }
        }
        catch (error) {
            console.error('❌ [checkout-store] Error validating discount code:', error);
            discountError.value = 'Fehler beim Validieren des Rabattcodes';
            discountInfo.value = null;
        }
        finally {
            discountValidating.value = false;
        }
    };
    const removeDiscountCode = async () => {
        clearDiscountCode();
        // ✅ WICHTIG: Clear discount from useOrderPricing store
        if (clearOrderPricingDiscount) {
            clearOrderPricingDiscount();
        }
        console.log('🗑️ [checkout-store] Discount code removed');
        // ✅ WICHTIG: Trigger order recalculation WITHOUT discount code
        console.log('🔄 [checkout-store] Triggering order recalculation without discount...');
        await calculateOrderPricing(cartStore.items, orderType.value, getDeliveryAddressForType(orderType.value), '' // ✅ Empty string = remove discount (undefined would keep stored code)
        );
        console.log('✅ [checkout-store] Order recalculated without discount');
    };
    const clearDiscountError = () => {
        discountError.value = '';
        console.log('🧹 [checkout-store] Discount error cleared');
    };
    const canSubmit = computed(() => {
        // Basic validation
        if (cartStore.items.length === 0) {
            console.log('🚫 Submit blocked: No items in cart');
            return false;
        }
        if (manualIsLoading.value) {
            console.log('🚫 Submit blocked: Loading in progress');
            return false;
        }
        // Customer data validation (required for all orders)
        if (!cartStore.customerData.name?.trim()) {
            console.log('🚫 Submit blocked: Customer name missing');
            return false;
        }
        if (!cartStore.customerData.phone?.trim()) {
            console.log('🚫 Submit blocked: Customer phone missing');
            return false;
        }
        if (!cartStore.customerData.email?.trim()) {
            console.log('🚫 Submit blocked: Customer email missing');
            return false;
        }
        // Delivery address validation (only for delivery orders)
        if (orderType.value === 'delivery') {
            if (!cartStore.deliveryAddress.street?.trim()) {
                console.log('🚫 Submit blocked: Delivery street missing');
                return false;
            }
            if (!cartStore.deliveryAddress.city?.trim()) {
                console.log('🚫 Submit blocked: Delivery city missing');
                return false;
            }
            if (!cartStore.deliveryAddress.postalCode?.trim()) {
                console.log('🚫 Submit blocked: Delivery postal code missing');
                return false;
            }
        }
        // Minimum order validation - only for delivery orders
        if (orderType.value === 'delivery' && !manualIsMinimumOrderReached.value) {
            console.log('🚫 Submit blocked: Minimum order not reached for delivery', {
                orderType: orderType.value,
                isReached: manualIsMinimumOrderReached.value,
                manualSubtotal: manualSubtotal.value
            });
            return false;
        }
        // Opening hours validation
        const pricingData = sharedPricing.pricingData.value;
        const openingHoursValidation = manualOpeningHours.value || pricingData?.data?.validation?.openingHours;
        if (openingHoursValidation && !openingHoursValidation.isWithinHours) {
            console.log('🚫 Submit blocked: Outside opening hours', {
                orderType: orderType.value,
                status: openingHoursValidation.status,
                message: openingHoursValidation.message,
                closingTime: openingHoursValidation.closingTime,
                alternativeMessage: openingHoursValidation.alternativeMessage
            });
            return false;
        }
        console.log('✅ Submit allowed:', {
            itemCount: cartStore.items.length,
            orderType: orderType.value,
            isMinOrderReached: manualIsMinimumOrderReached.value,
            subtotal: manualSubtotal.value,
            hasCustomerData: {
                name: !!cartStore.customerData.name?.trim(),
                phone: !!cartStore.customerData.phone?.trim(),
                email: !!cartStore.customerData.email?.trim()
            },
            hasDeliveryAddress: orderType.value === 'delivery' ? {
                street: !!cartStore.deliveryAddress.street?.trim(),
                city: !!cartStore.deliveryAddress.city?.trim(),
                postalCode: !!cartStore.deliveryAddress.postalCode?.trim()
            } : 'not_required'
        });
        return true;
    });
    // ✅ DEBOUNCE: Prevent multiple simultaneous pricing calculations
    let pricingDebounceTimer = null;
    let pricingCalculationPending = false;
    // ✅ USE SHARED SINGLETON - NO RACE CONDITIONS + DEBOUNCE
    const calculatePricing = async (forceRecalculate = false) => {
        if (cartStore.items.length === 0) {
            clearPricing();
            return;
        }
        console.log('🚀 Checkout Store: Pricing calculation requested', {
            orderType: orderType.value,
            itemCount: cartStore.items.length,
            forceRecalculate,
            hasPendingDebounce: !!pricingDebounceTimer,
            isCalculating: pricingCalculationPending
        });
        // ✅ CRITICAL FIX: Prevent multiple debounce timers from being created
        if (pricingCalculationPending) {
            console.log('⚠️ Checkout Store: Calculation already in progress - ignoring request');
            return;
        }
        // ✅ INFO: For delivery orders without address, we still calculate (with base fee only)
        // The UI will show "ab X€" for delivery fee instead of exact amount
        if (orderType.value === 'delivery') {
            const { street, city, postalCode } = cartStore.deliveryAddress;
            const hasCompleteAddress = street?.trim() && city?.trim() && postalCode?.trim();
            if (!hasCompleteAddress) {
                console.log('ℹ️ Checkout Store: Delivery order without address - will calculate base fee only (shows "ab X€")', {
                    street: !!street?.trim(),
                    city: !!city?.trim(),
                    postalCode: !!postalCode?.trim()
                });
            }
        }
        // ✅ DEBOUNCE: Clear existing debounce timer
        if (pricingDebounceTimer) {
            console.log('⏱️ Checkout Store: Clearing previous debounce timer');
            clearTimeout(pricingDebounceTimer);
            pricingDebounceTimer = null;
        }
        // ✅ MARK AS PENDING: Prevent new debounce timers while this one is queued
        pricingCalculationPending = true;
        // ✅ DEBOUNCE: Wait 150ms before executing to batch rapid changes
        pricingDebounceTimer = setTimeout(async () => {
            console.log('💰 Checkout Store: Executing debounced pricing calculation');
            try {
                // Use shared singleton method - handles all race conditions internally
                const deliveryAddressForPricing = getDeliveryAddressForType(orderType.value);
                console.log('📍 Checkout Store: Delivery address for pricing:', {
                    orderType: orderType.value,
                    address: deliveryAddressForPricing,
                    hasAddress: !!deliveryAddressForPricing
                });
                await calculateOrderPricing(cartStore.items, orderType.value, deliveryAddressForPricing);
                // ✅ Timing values are updated automatically by watch
                console.log('✅ Checkout Store: Shared pricing calculation completed', {
                    total: manualTotal.value,
                    isMinimumOrderReached: isMinimumOrderReached.value,
                    pickupTime: manualPickupTime.value,
                    deliveryTime: manualDeliveryTime.value
                });
            }
            finally {
                pricingCalculationPending = false;
                pricingDebounceTimer = null;
            }
        }, 150); // 150ms debounce - batches rapid changes but still feels instant
    };
    // ✅ TIME SELECTION FUNCTIONS
    const setSelectedTime = (time, orderTypeTarget) => {
        const targetType = orderTypeTarget || orderType.value;
        console.log('🕐 [checkout-store] Setting selected time (internal):', {
            time,
            targetType,
            currentOrderType: orderType.value
        });
        if (targetType === 'pickup') {
            selectedPickupTime.value = time;
        }
        else {
            selectedDeliveryTime.value = time;
        }
    };
    // ✅ TIME SELECTION WITH VALIDATION: Hauptfunktion für Zeit-Auswahl mit Server-Validierung
    const setSelectedTimeWithValidation = async (time, orderTypeTarget) => {
        const targetType = orderTypeTarget || orderType.value;
        console.log('🔍 [checkout-store] Setting time with validation:', {
            time,
            targetType,
            currentOrderType: orderType.value
        });
        // ✅ SCHRITT 1: Server-Validierung
        const validationResult = await validateSelectedTime(time, targetType);
        if (validationResult.isValid && validationResult.validatedTime) {
            // ✅ SCHRITT 2: Setze die validierte Zeit
            setSelectedTime(validationResult.validatedTime, targetType);
            console.log('✅ [checkout-store] Time set successfully:', {
                requestedTime: time,
                validatedTime: validationResult.validatedTime,
                targetType
            });
            return {
                success: true,
                validatedTime: validationResult.validatedTime
            };
        }
        else {
            console.log('❌ [checkout-store] Time validation failed:', validationResult.error);
            return {
                success: false,
                error: validationResult.error
            };
        }
    };
    const clearSelectedTime = (orderTypeTarget) => {
        const targetType = orderTypeTarget || orderType.value;
        console.log('🗑️ [checkout-store] Clearing selected time for:', targetType);
        if (targetType === 'pickup') {
            selectedPickupTime.value = '';
        }
        else {
            selectedDeliveryTime.value = '';
        }
    };
    // ✅ TIME VALIDATION: Server-Validierung für gewählte Zeit mit Toleranz
    const validateSelectedTime = async (time, orderTypeTarget) => {
        const targetType = orderTypeTarget || orderType.value;
        console.log('🔍 [checkout-store] Validating selected time:', {
            time,
            targetType,
            cartItems: cartStore.items.length
        });
        if (!time || cartStore.items.length === 0) {
            return { isValid: false, error: 'Keine Zeit oder Artikel im Warenkorb' };
        }
        timeValidationLoading.value = true;
        timeValidationError.value = null;
        try {
            // ✅ SCHRITT 1: Validiere die exakte Zeit
            console.log('🕐 Validating exact time:', time);
            let validationResult = await validateTimeWithServer(time, targetType);
            if (validationResult.isValid) {
                lastValidatedTime.value = {
                    time: validationResult.validatedTime || time,
                    orderType: targetType,
                    validatedAt: new Date()
                };
                console.log('✅ Time validation successful:', validationResult.validatedTime || time);
                return { isValid: true, validatedTime: validationResult.validatedTime || time };
            }
            // ✅ SCHRITT 2: Toleranz - versuche +1 Minute (für Minutenwechsel)
            const timeMinutes = timeToMinutes(time);
            const timePlusOne = minutesToTime(timeMinutes + 1);
            console.log('⏰ Exact time failed, trying +1 minute tolerance:', timePlusOne);
            validationResult = await validateTimeWithServer(timePlusOne, targetType);
            if (validationResult.isValid) {
                lastValidatedTime.value = {
                    time: validationResult.validatedTime || timePlusOne,
                    orderType: targetType,
                    validatedAt: new Date()
                };
                console.log('✅ Time validation successful with +1min tolerance:', validationResult.validatedTime || timePlusOne);
                return { isValid: true, validatedTime: validationResult.validatedTime || timePlusOne };
            }
            // ✅ SCHRITT 3: Fehler - Zeit ist nicht mehr verfügbar
            const errorMessage = validationResult.error || 'Gewählte Zeit ist nicht mehr verfügbar';
            timeValidationError.value = errorMessage;
            console.log('❌ Time validation failed:', errorMessage);
            return { isValid: false, error: errorMessage };
        }
        catch (error) {
            const errorMessage = `Zeitvalidierung fehlgeschlagen: ${error.message || error}`;
            timeValidationError.value = errorMessage;
            console.error('❌ Time validation error:', error);
            return { isValid: false, error: errorMessage };
        }
        finally {
            timeValidationLoading.value = false;
        }
    };
    // ✅ HELPER: Server-API Call für Zeitvalidierung
    const validateTimeWithServer = async (time, orderType) => {
        // Erstelle einen speziellen Request für die Zeitvalidierung
        const now = new Date();
        // ✅ ERSTE PRÜFUNG: Ist die gewünschte Zeit noch in der Zukunft?
        const nowMinutes = now.getHours() * 60 + now.getMinutes();
        const requestedMinutes = timeToMinutes(time);
        console.log('🕐 Validating time:', {
            requested: time,
            requestedMinutes,
            nowMinutes,
            diff: requestedMinutes - nowMinutes
        });
        // ✅ ZEIT IN VERGANGENHEIT: Sofort ablehnen
        if (requestedMinutes <= nowMinutes) {
            return { isValid: false, error: `Gewählte Zeit ${time} liegt in der Vergangenheit` };
        }
        // ✅ ÖFFNUNGSZEITEN-PRÜFUNG: Prüfe ob Zeit innerhalb der Öffnungszeiten liegt
        if (operatingHours.value?.length) {
            const isWithinOperatingHours = operatingHours.value.some((hours) => {
                const openMinutes = timeToMinutes(hours.open);
                const closeMinutes = timeToMinutes(hours.close);
                // ✅ TAGESUMBRUCH-BEHANDLUNG: Wenn Schließzeit < Öffnungszeit → über Mitternacht
                if (closeMinutes < openMinutes) {
                    // Restaurant schließt nach Mitternacht (z.B. 17:00 - 02:00)
                    return requestedMinutes >= openMinutes || requestedMinutes <= closeMinutes;
                }
                else {
                    // Normale Öffnungszeiten (z.B. 15:00 - 23:00)
                    return requestedMinutes >= openMinutes && requestedMinutes <= closeMinutes;
                }
            });
            if (!isWithinOperatingHours) {
                return { isValid: false, error: `Gewählte Zeit ${time} liegt außerhalb der Öffnungszeiten` };
            }
        }
        try {
            // Verwende das shared pricing system für Server-Prüfung
            const response = await calculateOrderPricing(cartStore.items, orderType, getDeliveryAddressForType(orderType));
            if (response && response.status === 'success') {
                // Extrahiere Server-Zeit (früheste verfügbare Zeit)
                const serverEarliestTime = extractTimeFromResponse(response, orderType);
                console.log('📡 Server response:', {
                    serverEarliestTime,
                    requestedTime: time,
                    orderType
                });
                if (serverEarliestTime) {
                    const serverMinutes = timeToMinutes(serverEarliestTime);
                    // ✅ LOGIK: Gewünschte Zeit ist später als Server-Minimum = OK
                    if (requestedMinutes >= serverMinutes) {
                        console.log('✅ Requested time is valid (later than server minimum)');
                        return { isValid: true, validatedTime: time }; // Verwende ursprünglich gewünschte Zeit
                    }
                    else {
                        // ✅ LOGIK: Gewünschte Zeit ist früher als Server-Minimum = Server-Zeit verwenden
                        console.log('⚠️ Requested time too early, using server time');
                        return { isValid: true, validatedTime: serverEarliestTime };
                    }
                }
                else {
                    // Server hat keine Zeit zurückgegeben, aber Pricing war erfolgreich
                    console.log('✅ Server validation successful, no specific time returned');
                    return { isValid: true, validatedTime: time };
                }
            }
            else {
                return { isValid: false, error: response?.message || 'Server-Validierung fehlgeschlagen' };
            }
        }
        catch (error) {
            return { isValid: false, error: error.message || 'API-Fehler bei Zeitvalidierung' };
        }
    };
    // ✅ HELPER: Zeit aus Server-Response extrahieren
    const extractTimeFromResponse = (response, orderType) => {
        const data = response.data;
        if (!data)
            return null;
        if (orderType === 'pickup' && data.delivery?.estimatedPickupTime?.display) {
            // Parse "21:55 Uhr" → "21:55"
            const match = data.delivery.estimatedPickupTime.display.match(/(\d{1,2}:\d{2})/);
            return match ? match[1] : null;
        }
        if (orderType === 'delivery' && data.delivery?.estimatedDeliveryTime?.display) {
            // Parse "21:55 - 22:40 Uhr" → "21:55" (Anfang)
            const match = data.delivery.estimatedDeliveryTime.display.match(/(\d{1,2}:\d{2})/);
            return match ? match[1] : null;
        }
        return null;
    };
    // ✅ HELPER: Zeit zu Minuten konvertieren
    const timeToMinutes = (time) => {
        const [hours, minutes] = time.split(':').map(Number);
        return hours * 60 + minutes;
    };
    // ✅ HELPER: Minuten zu Zeit konvertieren (mit Tagesumbruch-Behandlung)
    const minutesToTime = (minutes) => {
        // ✅ TAGESUMBRUCH: Wenn über 24 Stunden, auf nächsten Tag beschränken
        const dayMinutes = minutes % (24 * 60); // 1440 Minuten = 24 Stunden
        const hours = Math.floor(dayMinutes / 60);
        const mins = dayMinutes % 60;
        // ✅ VALIDIERUNG: Stunden sollten zwischen 0-23 sein
        const validHours = Math.max(0, Math.min(23, hours));
        return `${String(validHours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`;
    };
    // ✅ NEW: Clear order-related data but keep customer data
    const clearOrderData = () => {
        console.log('🗑️ [checkout-store] Clearing order data after successful submission');
        // Clear selected times
        selectedPickupTime.value = '';
        selectedDeliveryTime.value = '';
        // Clear payment data
        clearPaymentData();
        // Clear discount data
        clearDiscountCode();
        // Clear pricing data
        clearPricing();
        // Keep customer data (address, contact info) for next order
        // Keep order type for user convenience
        console.log('✅ [checkout-store] Order data cleared, customer data preserved');
    };
    const setOrderType = (type) => {
        if (orderType.value !== type) {
            console.log('🔄 Checkout Store: Changing order type', {
                from: orderType.value,
                to: type
            });
            orderType.value = type;
            cartStore.deliveryType = type; // sync with cart
            // Use shared singleton for recalculation
            calculatePricing(true);
        }
    };
    // ✅ AUTO-CALCULATE on store initialization if cart has items
    if (cartStore.items.length > 0) {
        // ✅ FIX: Only auto-calculate if we have the necessary data
        const shouldAutoCalculate = orderType.value === 'pickup' ||
            (orderType.value === 'delivery' &&
                cartStore.deliveryAddress?.street?.trim() &&
                cartStore.deliveryAddress?.city?.trim() &&
                cartStore.deliveryAddress?.postalCode?.trim());
        if (shouldAutoCalculate) {
            console.log('🚀 Checkout Store: Auto-calculating pricing on initialization', {
                itemCount: cartStore.items.length,
                orderType: orderType.value,
                hasDeliveryAddress: orderType.value === 'delivery' ?
                    !!(cartStore.deliveryAddress?.street?.trim()) : true
            });
            calculatePricing();
        }
        else {
            console.log('⏸️ Checkout Store: Skipping auto-calculate - waiting for delivery address', {
                itemCount: cartStore.items.length,
                orderType: orderType.value,
                hasStreet: !!(cartStore.deliveryAddress?.street?.trim()),
                hasCity: !!(cartStore.deliveryAddress?.city?.trim()),
                hasPostalCode: !!(cartStore.deliveryAddress?.postalCode?.trim())
            });
        }
    }
    // ✅ STARTE ZEIT-UPDATES beim Store-Init
    startTimeUpdates();
    // ✅ INITIAL DEBUG: Log store state
    console.log('🏪 [checkout-store] Store initialized:', {
        orderType: orderType.value,
        hasPaymentMethods: !!paymentMethods.value,
        initialPaymentMethods: paymentMethods.value
    });
    return {
        // ✅ SHARED SINGLETON STATE (reactive refs from shared composable)
        pricingData,
        isLoading,
        error,
        requestStatus,
        orderType,
        // ✅ MANUAL PRICING REFS (fixed reactivity)
        subtotal: manualSubtotal,
        deliveryFee: manualDeliveryFee,
        serviceFee: manualServiceFee,
        total: manualTotal,
        minimumOrderInfo,
        isMinimumOrderReached,
        // ✅ TAX DATA REFS (reactive tax information)
        taxBreakdown,
        taxCalculation,
        totalTax,
        // ✅ MANUAL TIMING REFS (fixed reactivity)
        pickupTime: manualPickupTime,
        deliveryTime: manualDeliveryTime,
        // ✅ DELIVERY ADDRESS STATE
        hasCompleteDeliveryAddress,
        // ✅ ESTIMATED TIMES (für Modal und exakte Zeitanzeige)
        estimatedPickupTime,
        estimatedDeliveryTime,
        estimatedPickupDateTime,
        estimatedDeliveryDateTime,
        // ✅ USER SELECTED TIMES (separate für Pickup/Delivery)
        selectedPickupTime,
        selectedDeliveryTime,
        setSelectedTime,
        setSelectedTimeWithValidation, // ✅ NEW: Zeit-Auswahl mit Server-Validierung
        clearSelectedTime,
        clearOrderData, // ✅ NEW: Clear order data after successful submission
        // ✅ TIME VALIDATION STATE
        timeValidationLoading,
        timeValidationError,
        lastValidatedTime,
        // ✅ OPERATING HOURS (für Modal)
        operatingHours,
        // ✅ ZENTRALE ZEITBERECHNUNG (neue API)
        currentTime,
        currentTimeWindow,
        canSubmit,
        // ✅ VALIDATION INFO
        openingHoursInfo: manualOpeningHours,
        // ✅ PAYMENT METHODS STATE
        paymentMethods,
        selectedPaymentMethod,
        paymentMethodsLoading,
        availablePaymentMethods,
        hasPaymentMethods,
        // ✅ PAYMENT METHODS ACTIONS
        selectPaymentMethod,
        clearPaymentData,
        getCardPaymentText,
        // ✅ DISCOUNT CODE STATE & ACTIONS
        discountCode,
        discountInfo,
        discountFromUrl,
        discountValidating,
        discountError,
        setDiscountCode,
        setDiscountFromUrl,
        clearDiscountCode,
        updateDiscountInfo,
        applyDiscountCode,
        removeDiscountCode,
        clearDiscountError,
        // ✅ ACTIONS (uses shared methods)
        calculatePricing,
        clearPricing,
        setOrderType
    };
});
